// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";

export function TicketDetail({ ticket, userId, onClose, closing }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("support_messages")
      .select("id, sender, message, created_at")
      .eq("ticket_id", ticket.id)
      .order("created_at", { ascending: true })
      .then(({ data }) => {
        if (cancelled) return;
        setMessages(data || []);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [ticket.id]);

  async function send() {
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    setErr(null);
    const { data, error } = await supabase
      .from("support_messages")
      .insert({ ticket_id: ticket.id, sender: "user", message: body })
      .select()
      .single();
    setSending(false);
    if (error) { setErr(error.message); return; }
    setMessages((prev) => [...prev, data]);
    setReply("");
  }

  const closed = ticket.status === "closed" || ticket.status === "resolved";

  return (
    <div style={{ padding: "4px 22px 18px", background: C.cream }}>
      {/* Original issue */}
      {ticket.description && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>Your message</div>
          <div style={{ fontSize: 13, color: C.slate, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{ticket.description}</div>
        </div>
      )}

      {/* Support resolution note (admin-written summary) */}
      {ticket.resolution_note && (
        <div style={{ marginBottom: 12, padding: 12, background: C.white, borderRadius: 10, border: `1px solid ${C.dustDk}` }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, color: C.greenDk, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>Support response</div>
          <div style={{ fontSize: 13, color: C.charcoal, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{ticket.resolution_note}</div>
          {ticket.resolved_at && (
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Resolved {timeAgo(ticket.resolved_at)}</div>
          )}
        </div>
      )}

      {/* Conversation thread */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Conversation</div>
        {loading ? (
          <Skeleton h={40} />
        ) : messages.length === 0 ? (
          <div style={{ fontSize: 12, color: C.muted }}>No replies yet. Our team will respond soon.</div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {messages.map((m) => {
              const isMe = m.sender === "user" || m.sender === "customer";
              return (
                <div
                  key={m.id}
                  style={{
                    padding: 10,
                    background: isMe ? C.hunter : C.white,
                    color: isMe ? C.dust : C.charcoal,
                    borderRadius: 10,
                    border: isMe ? "none" : `1px solid ${C.dustDk}`,
                    alignSelf: isMe ? "flex-end" : "flex-start",
                    maxWidth: "85%",
                    marginLeft: isMe ? "auto" : 0,
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 800, opacity: 0.7, marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {isMe ? "You" : "Support"} · {timeAgo(m.created_at)}
                  </div>
                  <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.4 }}>{m.message}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reply form */}
      {!closed && (
        <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Add a reply…"
            rows={2}
            style={{ ...inputStyle, resize: "vertical", background: C.white }}
          />
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Btn onClick={send} disabled={sending || !reply.trim()} size="sm">
              {sending ? "Sending…" : "Send reply"}
            </Btn>
            <Btn onClick={onClose} disabled={closing} kind="outline" size="sm">
              {closing ? "Closing…" : "Close ticket"}
            </Btn>
            {err && <span style={{ fontSize: 12, color: C.red, fontWeight: 700 }}>{err}</span>}
          </div>
        </div>
      )}

      {closed && (
        <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>
          This ticket is {ticket.status}. Open a new ticket if you need more help.
        </div>
      )}
    </div>
  );
}

export function SupportPanel({ user }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ subject: "", category: "general", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [closing, setClosing] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    supabase
      .from("support_tickets")
      .select("id, ticket_number, subject, category, description, status, priority, resolution_note, created_at, updated_at, resolved_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data }) => {
        if (cancelled) return;
        setTickets(data || []);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  async function submit() {
    if (!form.subject.trim() || !form.description.trim()) return;
    setSubmitting(true);
    setMsg(null);
    // ticket_number is auto-generated by the `set_ticket_number` BEFORE INSERT
    // trigger (migration 20260409000001). Don't send it from the client.
    const { data, error } = await supabase
      .from("support_tickets")
      .insert({
        user_id: user.id,
        subject: form.subject.trim(),
        category: form.category,
        description: form.description.trim(),
        priority: "normal",
        status: "open",
      })
      .select()
      .single();
    setSubmitting(false);
    if (error) {
      setMsg({ tone: "red", text: error.message });
      return;
    }
    setTickets((prev) => [data, ...prev]);
    setForm({ subject: "", category: "general", description: "" });
    setMsg({ tone: "green", text: `Ticket ${data.ticket_number || ""} opened — we'll be in touch.` });
  }

  async function closeTicket(id) {
    setClosing(id);
    const ts = new Date().toISOString();
    const { error } = await supabase
      .from("support_tickets")
      .update({ status: "closed", updated_at: ts })
      .eq("id", id);
    setClosing(null);
    if (error) {
      setMsg({ tone: "red", text: error.message });
      return;
    }
    setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status: "closed", updated_at: ts } : t)));
  }

  const statusTint = { open: C.hunter, in_progress: "#B45309", resolved: C.greenDk, closed: C.muted };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card pad={22}>
        <Eyebrow>Open a ticket</Eyebrow>
        <div style={{ display: "grid", gap: 12, marginTop: 10 }}>
          <Field label="Category">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={inputStyle}>
              <option value="general">General question</option>
              <option value="order">Order issue</option>
              <option value="payment">Payment issue</option>
              <option value="refund">Refund request</option>
              <option value="account">Account & login</option>
              <option value="bug">Report a bug</option>
            </select>
          </Field>
          <Field label="Subject"><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} style={inputStyle} /></Field>
          <Field label="Describe the issue"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} style={{ ...inputStyle, resize: "vertical" }} /></Field>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Btn onClick={submit} disabled={submitting || !form.subject.trim() || !form.description.trim()}>
              {submitting ? "Sending…" : "Submit ticket"}
            </Btn>
            {msg && <span style={{ fontSize: 12, fontWeight: 700, color: msg.tone === "red" ? C.red : C.greenDk }}>{msg.text}</span>}
          </div>
        </div>
      </Card>
      <Card pad={0}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.cream}` }}>
          <Eyebrow>Your tickets</Eyebrow>
        </div>
        {loading ? <div style={{ padding: 20 }}><Skeleton h={60} /></div> : tickets.length === 0 ? (
          <div style={{ padding: 20, fontSize: 12.5, color: C.muted }}>No tickets yet.</div>
        ) : (
          tickets.map((t) => {
            const isOpen = openId === t.id;
            return (
              <div key={t.id} style={{ borderBottom: `1px solid ${C.cream}` }}>
                <div
                  onClick={() => setOpenId(isOpen ? null : t.id)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 22px", cursor: "pointer", background: isOpen ? C.cream : "transparent" }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>{t.subject}</div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                      {t.ticket_number ? `#${t.ticket_number} · ` : ""}{t.category} · {timeAgo(t.created_at)}
                    </div>
                  </div>
                  <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 8px", borderRadius: 6, background: C.white, color: statusTint[t.status] || C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>
                    {t.status?.replace("_", " ")}
                  </span>
                  <Icon name={isOpen ? "chevron-up" : "chevron-down"} size={14} color={C.muted} />
                </div>
                {isOpen && (
                  <TicketDetail
                    ticket={t}
                    userId={user.id}
                    onClose={() => closeTicket(t.id)}
                    closing={closing === t.id}
                  />
                )}
              </div>
            );
          })
        )}
      </Card>
    </div>
  );
}

// =============================================================================
// ABOUT
// =============================================================================
